// src/pages/trading-portal/OptionsContractManagement/ContractDiscoveryPage.js
// Options Contract Discovery page with 5-tab interface

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    Tabs,
    Tab,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Sync as SyncIcon,
    Calculate as CalculateIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    UnderlyingAssetType,
    UnderlyingAssetTypeLabels,
    UnderlyingAssetTypeColors,
    LiquidityTier,
    LiquidityTierLabels,
    LiquidityTierColors,
    ExpirationCycleType,
    ExpirationCycleTypeLabels,
    ExpirationCycleTypeColors,
    StrikeSelectionMethod,
    StrikeSelectionMethodLabels,
    StrategyType,
    StrategyTypeLabels,
    OptionType,
    OptionTypeLabels,
    OptionTypeColors,
    ExerciseStyle,
    ExerciseStyleLabels,
    ExerciseStyleColors,
    SettlementType,
    SettlementTypeLabels,
    SettlementTypeColors,
    Sector,
    SectorLabels,
    Exchange,
    ExchangeLabels,
    TradingHours,
    TradingHoursLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const ContractDiscoveryPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared
    const [symbolOptions, setSymbolOptions] = useState([]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // ==================== TAB 0: OPTIONS UNIVERSE ====================
    const [universeData, setUniverseData] = useState([]);
    const [universeLoading, setUniverseLoading] = useState(false);
    const [universePage, setUniversePage] = useState(0);
    const [universePerPage, setUniversePerPage] = useState(10);
    const [universeTotal, setUniverseTotal] = useState(0);
    const [universeSearch, setUniverseSearch] = useState('');
    const [universeLiquidityFilter, setUniverseLiquidityFilter] = useState('');
    const [universeAssetTypeFilter, setUniverseAssetTypeFilter] = useState('');
    const [universeDialog, setUniverseDialog] = useState(false);
    const [universeForm, setUniverseForm] = useState({});
    const [universeEditing, setUniverseEditing] = useState(null);
    const [universeDeleteDialog, setUniverseDeleteDialog] = useState(false);
    const [universeDeleteTarget, setUniverseDeleteTarget] = useState(null);
    const [scanningId, setScanningId] = useState(null);

    // ==================== TAB 1: EXPIRATION CALENDAR ====================
    const [calendarData, setCalendarData] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarPage, setCalendarPage] = useState(0);
    const [calendarPerPage, setCalendarPerPage] = useState(10);
    const [calendarTotal, setCalendarTotal] = useState(0);
    const [calendarSymbolFilter, setCalendarSymbolFilter] = useState(null);
    const [calendarDateFrom, setCalendarDateFrom] = useState('');
    const [calendarDateTo, setCalendarDateTo] = useState('');
    const [calendarDialog, setCalendarDialog] = useState(false);
    const [calendarForm, setCalendarForm] = useState({});
    const [syncingCalendar, setSyncingCalendar] = useState(false);

    // ==================== TAB 2: STRIKE SELECTION ====================
    const [strikeData, setStrikeData] = useState([]);
    const [strikeLoading, setStrikeLoading] = useState(false);
    const [strikePage, setStrikePage] = useState(0);
    const [strikePerPage, setStrikePerPage] = useState(10);
    const [strikeTotal, setStrikeTotal] = useState(0);
    const [strikeSymbolFilter, setStrikeSymbolFilter] = useState(null);
    const [strikeDialog, setStrikeDialog] = useState(false);
    const [strikeForm, setStrikeForm] = useState({});

    // ==================== TAB 3: CONTRACT SPECIFICATIONS ====================
    const [specsData, setSpecsData] = useState([]);
    const [specsLoading, setSpecsLoading] = useState(false);
    const [specsPage, setSpecsPage] = useState(0);
    const [specsPerPage, setSpecsPerPage] = useState(10);
    const [specsTotal, setSpecsTotal] = useState(0);
    const [specsDialog, setSpecsDialog] = useState(false);
    const [specsForm, setSpecsForm] = useState({});
    const [specsEditing, setSpecsEditing] = useState(null);
    const [specsDeleteDialog, setSpecsDeleteDialog] = useState(false);
    const [specsDeleteTarget, setSpecsDeleteTarget] = useState(null);

    // ==================== TAB 4: LIQUIDITY SCORES ====================
    const [liquidityData, setLiquidityData] = useState([]);
    const [liquidityLoading, setLiquidityLoading] = useState(false);
    const [liquidityPage, setLiquidityPage] = useState(0);
    const [liquidityPerPage, setLiquidityPerPage] = useState(10);
    const [liquidityTotal, setLiquidityTotal] = useState(0);
    const [liquiditySymbolFilter, setLiquiditySymbolFilter] = useState(null);
    const [liquidityExpDateFilter, setLiquidityExpDateFilter] = useState('');
    const [liquidityTierFilter, setLiquidityTierFilter] = useState('');
    const [liquidityCalcDialog, setLiquidityCalcDialog] = useState(false);
    const [liquidityCalcForm, setLiquidityCalcForm] = useState({});

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-ocm-contract-discovery', 'view'),
                hasPermission('tr-ocm-contract-discovery', 'add'),
                hasPermission('tr-ocm-contract-discovery', 'edit'),
                hasPermission('tr-ocm-contract-discovery', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
        fetchSymbols();
    }, []);

    // ==================== FETCH SYMBOLS ====================
    const fetchSymbols = async () => {
        try {
            const res = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            setSymbolOptions(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    };

    // ==================== FORMAT HELPERS ====================
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatPercent = (val) => val != null ? `${(parseFloat(val) * 100).toFixed(2)}%` : '-';
    const formatDecimal = (val, digits = 4) => val != null ? parseFloat(val).toFixed(digits) : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

    // ==================== TAB 0: OPTIONS UNIVERSE HANDLERS ====================

    const fetchUniverse = useCallback(async () => {
        if (!canView) return;
        setUniverseLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: universePage + 1, per_page: universePerPage });
            if (universeSearch) params.append('search', universeSearch);
            if (universeLiquidityFilter) params.append('liquidity_tier', universeLiquidityFilter);
            if (universeAssetTypeFilter) params.append('underlying_asset_type', universeAssetTypeFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/universe?${params.toString()}`);
            if (res.data.success) {
                setUniverseData(res.data.data || []);
                setUniverseTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch universe data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch universe data');
        } finally {
            setUniverseLoading(false);
        }
    }, [canView, universePage, universePerPage, universeSearch, universeLiquidityFilter, universeAssetTypeFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchUniverse();
    }, [fetchUniverse, canView, activeTab]);

    const handleUniverseOpenDialog = (row = null) => {
        if (row) {
            setUniverseEditing(row);
            const matchedSymbol = symbolOptions.find(s => s.id === row.symbol_id) || null;
            setUniverseForm({
                symbol_id: matchedSymbol,
                underlying_asset_type: row.underlying_asset_type || '',
                sector: row.sector || '',
                has_weeklies: row.has_weeklies || false,
                has_quarterlies: row.has_quarterlies || false,
                scan_config: row.scan_config || '',
            });
        } else {
            setUniverseEditing(null);
            setUniverseForm({
                symbol_id: null,
                underlying_asset_type: '',
                sector: '',
                has_weeklies: false,
                has_quarterlies: false,
                scan_config: '',
            });
        }
        setUniverseDialog(true);
    };

    const handleUniverseSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: universeForm.symbol_id?.id || universeForm.symbol_id,
                underlying_asset_type: universeForm.underlying_asset_type,
                sector: universeForm.sector,
                has_weeklies: universeForm.has_weeklies,
                has_quarterlies: universeForm.has_quarterlies,
                scan_config: universeForm.scan_config,
            };
            let res;
            if (universeEditing) {
                res = await axiosInstance.put(`/api/options-contract-management/universe/${universeEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/options-contract-management/universe', payload);
            }
            if (res.data.success) {
                setUniverseDialog(false);
                fetchUniverse();
            } else {
                setError(res.data.message || 'Failed to save universe entry');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save universe entry');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUniverseDelete = async () => {
        if (!universeDeleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/options-contract-management/universe/${universeDeleteTarget.id}`);
            if (res.data.success) {
                setUniverseDeleteDialog(false);
                setUniverseDeleteTarget(null);
                fetchUniverse();
            } else {
                setError(res.data.message || 'Failed to delete universe entry');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete universe entry');
        }
    };

    const handleUniverseScan = async (symbolId) => {
        setScanningId(symbolId);
        try {
            await axiosInstance.post('/api/options-contract-management/universe/scan', { symbol_id: symbolId });
            fetchUniverse();
        } catch (err) {
            setError(err.response?.data?.message || 'Scan failed');
        } finally {
            setScanningId(null);
        }
    };

    // ==================== TAB 1: EXPIRATION CALENDAR HANDLERS ====================

    const fetchCalendar = useCallback(async () => {
        if (!canView) return;
        setCalendarLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: calendarPage + 1, per_page: calendarPerPage });
            if (calendarSymbolFilter) params.append('symbol_id', calendarSymbolFilter.id);
            if (calendarDateFrom) params.append('date_from', calendarDateFrom);
            if (calendarDateTo) params.append('date_to', calendarDateTo);
            const res = await axiosInstance.get(`/api/options-contract-management/expiration-calendar?${params.toString()}`);
            if (res.data.success) {
                setCalendarData(res.data.data || []);
                setCalendarTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch expiration calendar');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch expiration calendar');
        } finally {
            setCalendarLoading(false);
        }
    }, [canView, calendarPage, calendarPerPage, calendarSymbolFilter, calendarDateFrom, calendarDateTo]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchCalendar();
    }, [fetchCalendar, canView, activeTab]);

    const handleCalendarOpenDialog = () => {
        setCalendarForm({
            symbol_id: null,
            expiration_date: '',
            cycle_type: '',
        });
        setCalendarDialog(true);
    };

    const handleCalendarSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: calendarForm.symbol_id?.id || calendarForm.symbol_id,
                expiration_date: calendarForm.expiration_date,
                cycle_type: calendarForm.cycle_type,
            };
            const res = await axiosInstance.post('/api/options-contract-management/expiration-calendar', payload);
            if (res.data.success) {
                setCalendarDialog(false);
                fetchCalendar();
            } else {
                setError(res.data.message || 'Failed to add calendar entry');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add calendar entry');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCalendarSync = async () => {
        if (!calendarSymbolFilter) return;
        setSyncingCalendar(true);
        try {
            await axiosInstance.post('/api/options-contract-management/expiration-calendar/sync', {
                symbol_id: calendarSymbolFilter.id,
            });
            fetchCalendar();
        } catch (err) {
            setError(err.response?.data?.message || 'Calendar sync failed');
        } finally {
            setSyncingCalendar(false);
        }
    };

    // ==================== TAB 2: STRIKE SELECTION HANDLERS ====================

    const fetchStrikeSelection = useCallback(async () => {
        if (!canView) return;
        setStrikeLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: strikePage + 1, per_page: strikePerPage });
            if (strikeSymbolFilter) params.append('symbol_id', strikeSymbolFilter.id);
            const res = await axiosInstance.get(`/api/options-contract-management/strike-selection?${params.toString()}`);
            if (res.data.success) {
                setStrikeData(res.data.data || []);
                setStrikeTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch strike selections');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch strike selections');
        } finally {
            setStrikeLoading(false);
        }
    }, [canView, strikePage, strikePerPage, strikeSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchStrikeSelection();
    }, [fetchStrikeSelection, canView, activeTab]);

    const handleStrikeOpenDialog = () => {
        setStrikeForm({
            symbol_id: null,
            strategy_type: '',
            selection_method: '',
            option_type: '',
            target_delta: '',
            target_pop: '',
            target_risk_reward: '',
            expiration_date: '',
        });
        setStrikeDialog(true);
    };

    const handleStrikeSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: strikeForm.symbol_id?.id || strikeForm.symbol_id,
                strategy_type: strikeForm.strategy_type,
                selection_method: strikeForm.selection_method,
                option_type: strikeForm.option_type,
                target_delta: strikeForm.target_delta ? parseFloat(strikeForm.target_delta) : null,
                target_pop: strikeForm.target_pop ? parseFloat(strikeForm.target_pop) : null,
                target_risk_reward: strikeForm.target_risk_reward ? parseFloat(strikeForm.target_risk_reward) : null,
                expiration_date: strikeForm.expiration_date || null,
            };
            const res = await axiosInstance.post('/api/options-contract-management/strike-selection', payload);
            if (res.data.success) {
                setStrikeDialog(false);
                fetchStrikeSelection();
            } else {
                setError(res.data.message || 'Failed to run strike selection');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to run strike selection');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 3: CONTRACT SPECIFICATIONS HANDLERS ====================

    const fetchSpecs = useCallback(async () => {
        if (!canView) return;
        setSpecsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: specsPage + 1, per_page: specsPerPage });
            const res = await axiosInstance.get(`/api/options-contract-management/contract-specs?${params.toString()}`);
            if (res.data.success) {
                setSpecsData(res.data.data || []);
                setSpecsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch contract specifications');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch contract specifications');
        } finally {
            setSpecsLoading(false);
        }
    }, [canView, specsPage, specsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchSpecs();
    }, [fetchSpecs, canView, activeTab]);

    const handleSpecsOpenDialog = (row = null) => {
        if (row) {
            setSpecsEditing(row);
            const matchedSymbol = symbolOptions.find(s => s.id === row.symbol_id) || null;
            setSpecsForm({
                symbol_id: matchedSymbol,
                underlying_asset_type: row.underlying_asset_type || '',
                exercise_style: row.exercise_style || '',
                settlement_type: row.settlement_type || '',
                contract_multiplier: row.contract_multiplier ?? 100,
                tick_size: row.tick_size ?? 0.01,
                exchange: row.exchange || '',
                currency: row.currency || 'USD',
                trading_hours: row.trading_hours || '',
                position_limit: row.position_limit ?? '',
            });
        } else {
            setSpecsEditing(null);
            setSpecsForm({
                symbol_id: null,
                underlying_asset_type: '',
                exercise_style: '',
                settlement_type: '',
                contract_multiplier: 100,
                tick_size: 0.01,
                exchange: '',
                currency: 'USD',
                trading_hours: '',
                position_limit: '',
            });
        }
        setSpecsDialog(true);
    };

    const handleSpecsSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: specsForm.symbol_id?.id || specsForm.symbol_id,
                underlying_asset_type: specsForm.underlying_asset_type,
                exercise_style: specsForm.exercise_style,
                settlement_type: specsForm.settlement_type,
                contract_multiplier: parseFloat(specsForm.contract_multiplier),
                tick_size: parseFloat(specsForm.tick_size),
                exchange: specsForm.exchange,
                currency: specsForm.currency,
                trading_hours: specsForm.trading_hours,
                position_limit: specsForm.position_limit ? parseInt(specsForm.position_limit, 10) : null,
            };
            let res;
            if (specsEditing) {
                res = await axiosInstance.put(`/api/options-contract-management/contract-specs/${specsEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/options-contract-management/contract-specs', payload);
            }
            if (res.data.success) {
                setSpecsDialog(false);
                fetchSpecs();
            } else {
                setError(res.data.message || 'Failed to save contract spec');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save contract spec');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSpecsDelete = async () => {
        if (!specsDeleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/options-contract-management/contract-specs/${specsDeleteTarget.id}`);
            if (res.data.success) {
                setSpecsDeleteDialog(false);
                setSpecsDeleteTarget(null);
                fetchSpecs();
            } else {
                setError(res.data.message || 'Failed to delete contract spec');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete contract spec');
        }
    };

    // ==================== TAB 4: LIQUIDITY SCORES HANDLERS ====================

    const fetchLiquidity = useCallback(async () => {
        if (!canView) return;
        setLiquidityLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: liquidityPage + 1, per_page: liquidityPerPage });
            if (liquiditySymbolFilter) params.append('symbol_id', liquiditySymbolFilter.id);
            if (liquidityExpDateFilter) params.append('expiration_date', liquidityExpDateFilter);
            if (liquidityTierFilter) params.append('liquidity_tier', liquidityTierFilter);
            const res = await axiosInstance.get(`/api/options-contract-management/liquidity-scores?${params.toString()}`);
            if (res.data.success) {
                setLiquidityData(res.data.data || []);
                setLiquidityTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch liquidity scores');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch liquidity scores');
        } finally {
            setLiquidityLoading(false);
        }
    }, [canView, liquidityPage, liquidityPerPage, liquiditySymbolFilter, liquidityExpDateFilter, liquidityTierFilter]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchLiquidity();
    }, [fetchLiquidity, canView, activeTab]);

    const handleLiquidityCalcOpen = () => {
        setLiquidityCalcForm({
            symbol_id: null,
            expiration_date: '',
        });
        setLiquidityCalcDialog(true);
    };

    const handleLiquidityCalcSave = async () => {
        setSubmitting(true);
        try {
            const payload = {
                symbol_id: liquidityCalcForm.symbol_id?.id || liquidityCalcForm.symbol_id,
            };
            if (liquidityCalcForm.expiration_date) {
                payload.expiration_date = liquidityCalcForm.expiration_date;
            }
            const res = await axiosInstance.post('/api/options-contract-management/liquidity-scores', payload);
            if (res.data.success) {
                setLiquidityCalcDialog(false);
                setLiquiditySymbolFilter(liquidityCalcForm.symbol_id || null);
                setLiquidityExpDateFilter('');
                setLiquidityTierFilter('');
                setLiquidityPage(0);
            } else {
                setError(res.data.message || 'Failed to calculate liquidity scores');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate liquidity scores');
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== PERMISSION GATE ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view Contract Discovery.
                </Alert>
            </Box>
        );
    }

    // ==================== TAB 0 RENDER: OPTIONS UNIVERSE ====================

    const renderOptionsUniverse = () => (
        <>
            {/* Filters & Actions Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Symbol"
                            value={universeSearch}
                            onChange={(e) => { setUniverseSearch(e.target.value); setUniversePage(0); }}
                            InputProps={{ startAdornment: <SearchIcon sx={{ color: '#9ca3af', mr: 1 }} /> }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Liquidity Tier</InputLabel>
                            <Select
                                value={universeLiquidityFilter}
                                label="Liquidity Tier"
                                onChange={(e) => { setUniverseLiquidityFilter(e.target.value); setUniversePage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(LiquidityTier).map(tier => (
                                    <MenuItem key={tier} value={tier}>{LiquidityTierLabels[tier]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Asset Type</InputLabel>
                            <Select
                                value={universeAssetTypeFilter}
                                label="Asset Type"
                                onChange={(e) => { setUniverseAssetTypeFilter(e.target.value); setUniversePage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(UnderlyingAssetType).map(type => (
                                    <MenuItem key={type} value={type}>{UnderlyingAssetTypeLabels[type]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchUniverse}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleUniverseOpenDialog()}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Symbol
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Universe Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Asset Type</TableCell>
                            <TableCell>Sector</TableCell>
                            <TableCell align="right">Avg Daily Volume</TableCell>
                            <TableCell align="right">Avg Open Interest</TableCell>
                            <TableCell align="right">Avg Bid-Ask Spread</TableCell>
                            <TableCell align="right">Liquidity Score</TableCell>
                            <TableCell>Liquidity Tier</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell>Last Scanned</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {universeLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : universeData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <SearchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No options universe entries found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            universeData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={UnderlyingAssetTypeLabels[row.underlying_asset_type] || row.underlying_asset_type || '-'}
                                            color={UnderlyingAssetTypeColors[row.underlying_asset_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{SectorLabels[row.sector] || row.sector || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.avg_daily_volume)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.avg_open_interest)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.avg_bid_ask_spread, 4)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.liquidity_score, 2)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={LiquidityTierLabels[row.liquidity_tier] || row.liquidity_tier || '-'}
                                            color={LiquidityTierColors[row.liquidity_tier] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_active ? 'Active' : 'Inactive'}
                                            color={row.is_active ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDateTime(row.last_scanned_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            <Tooltip title="Scan">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleUniverseScan(row.symbol_id)}
                                                    disabled={scanningId === row.symbol_id}
                                                >
                                                    {scanningId === row.symbol_id ? <CircularProgress size={18} /> : <SyncIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            {canEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleUniverseOpenDialog(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => { setUniverseDeleteTarget(row); setUniverseDeleteDialog(true); }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={universeTotal}
                page={universePage}
                onPageChange={(e, p) => setUniversePage(p)}
                rowsPerPage={universePerPage}
                onRowsPerPageChange={(e) => { setUniversePerPage(parseInt(e.target.value, 10)); setUniversePage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily }}
            />
        </>
    );

    // ==================== TAB 1 RENDER: EXPIRATION CALENDAR ====================

    const renderExpirationCalendar = () => (
        <>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={symbolOptions}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={calendarSymbolFilter}
                            onChange={(e, newValue) => { setCalendarSymbolFilter(newValue); setCalendarPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Symbol" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Date From"
                            type="date"
                            value={calendarDateFrom}
                            onChange={(e) => { setCalendarDateFrom(e.target.value); setCalendarPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Date To"
                            type="date"
                            value={calendarDateTo}
                            onChange={(e) => { setCalendarDateTo(e.target.value); setCalendarPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchCalendar}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Sync expiration calendar for selected symbol">
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={syncingCalendar ? <CircularProgress size={18} /> : <SyncIcon />}
                                    onClick={handleCalendarSync}
                                    disabled={!calendarSymbolFilter || syncingCalendar}
                                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                                >
                                    Sync
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCalendarOpenDialog}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Add Entry
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Calendar Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Cycle Type</TableCell>
                            <TableCell align="right">DTE</TableCell>
                            <TableCell align="right">Num Calls</TableCell>
                            <TableCell align="right">Num Puts</TableCell>
                            <TableCell align="right">Total Call OI</TableCell>
                            <TableCell align="right">Total Put OI</TableCell>
                            <TableCell align="right">Max Pain Strike</TableCell>
                            <TableCell>Significant</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {calendarLoading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : calendarData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                    <SearchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No expiration calendar entries found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            calendarData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExpirationCycleTypeLabels[row.cycle_type] || row.cycle_type || '-'}
                                            color={ExpirationCycleTypeColors[row.cycle_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.dte ?? '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.num_calls)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.num_puts)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.total_call_oi)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.total_put_oi)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.max_pain_strike)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.is_significant ? 'Yes' : 'No'}
                                            color={row.is_significant ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={calendarTotal}
                page={calendarPage}
                onPageChange={(e, p) => setCalendarPage(p)}
                rowsPerPage={calendarPerPage}
                onRowsPerPageChange={(e) => { setCalendarPerPage(parseInt(e.target.value, 10)); setCalendarPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily }}
            />
        </>
    );

    // ==================== TAB 2 RENDER: STRIKE SELECTION ====================

    const renderStrikeSelection = () => (
        <>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={symbolOptions}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={strikeSymbolFilter}
                            onChange={(e, newValue) => { setStrikeSymbolFilter(newValue); setStrikePage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Symbol" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchStrikeSelection}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handleStrikeOpenDialog}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Run Selection
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Strike Selection Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Strategy Type</TableCell>
                            <TableCell>Selection Method</TableCell>
                            <TableCell align="right">Target Delta</TableCell>
                            <TableCell align="right">Rec. Strike</TableCell>
                            <TableCell>Rec. Expiration</TableCell>
                            <TableCell>Option Type</TableCell>
                            <TableCell align="right">Rec. Premium</TableCell>
                            <TableCell align="right">Expected PoP</TableCell>
                            <TableCell align="right">Liquidity Score</TableCell>
                            <TableCell>Created At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {strikeLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : strikeData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <CalculateIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No strike selections found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            strikeData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{StrategyTypeLabels[row.strategy_type] || row.strategy_type || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{StrikeSelectionMethodLabels[row.selection_method] || row.selection_method || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatDecimal(row.target_delta, 2)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.recommended_strike)}</TableCell>
                                    <TableCell>{formatDate(row.recommended_expiration)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={OptionTypeLabels[row.option_type] || row.option_type || '-'}
                                            color={OptionTypeColors[row.option_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.recommended_premium)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.expected_pop)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.liquidity_score_at_strike, 2)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDateTime(row.created_at)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={strikeTotal}
                page={strikePage}
                onPageChange={(e, p) => setStrikePage(p)}
                rowsPerPage={strikePerPage}
                onRowsPerPageChange={(e) => { setStrikePerPage(parseInt(e.target.value, 10)); setStrikePage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily }}
            />
        </>
    );

    // ==================== TAB 3 RENDER: CONTRACT SPECIFICATIONS ====================

    const renderContractSpecs = () => (
        <>
            {/* Action Bar */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchSpecs}><RefreshIcon /></IconButton>
                </Tooltip>
                {canAdd && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleSpecsOpenDialog()}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Add Spec
                    </Button>
                )}
            </Box>

            {/* Specs Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Asset Type</TableCell>
                            <TableCell>Exercise Style</TableCell>
                            <TableCell>Settlement</TableCell>
                            <TableCell align="right">Multiplier</TableCell>
                            <TableCell align="right">Tick Size</TableCell>
                            <TableCell>Exchange</TableCell>
                            <TableCell>Currency</TableCell>
                            <TableCell>Trading Hours</TableCell>
                            <TableCell align="right">Position Limit</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {specsLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : specsData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    <SearchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No contract specifications found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            specsData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={UnderlyingAssetTypeLabels[row.underlying_asset_type] || row.underlying_asset_type || '-'}
                                            color={UnderlyingAssetTypeColors[row.underlying_asset_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={ExerciseStyleLabels[row.exercise_style] || row.exercise_style || '-'}
                                            color={ExerciseStyleColors[row.exercise_style] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={SettlementTypeLabels[row.settlement_type] || row.settlement_type || '-'}
                                            color={SettlementTypeColors[row.settlement_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.contract_multiplier ?? '-'}</TableCell>
                                    <TableCell align="right">{row.tick_size != null ? parseFloat(row.tick_size).toFixed(4) : '-'}</TableCell>
                                    <TableCell>{row.exchange || '-'}</TableCell>
                                    <TableCell>{row.currency || '-'}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {row.trading_hours || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatNumber(row.position_limit)}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {canEdit && (
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleSpecsOpenDialog(row)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canDelete && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => { setSpecsDeleteTarget(row); setSpecsDeleteDialog(true); }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={specsTotal}
                page={specsPage}
                onPageChange={(e, p) => setSpecsPage(p)}
                rowsPerPage={specsPerPage}
                onRowsPerPageChange={(e) => { setSpecsPerPage(parseInt(e.target.value, 10)); setSpecsPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily }}
            />
        </>
    );

    // ==================== TAB 4 RENDER: LIQUIDITY SCORES ====================

    const renderLiquidityScores = () => (
        <>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={symbolOptions}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            value={liquiditySymbolFilter}
                            onChange={(e, newValue) => { setLiquiditySymbolFilter(newValue); setLiquidityPage(0); }}
                            renderInput={(params) => (
                                <TextField {...params} label="Symbol" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Expiration Date"
                            type="date"
                            value={liquidityExpDateFilter}
                            onChange={(e) => { setLiquidityExpDateFilter(e.target.value); setLiquidityPage(0); }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Liquidity Tier</InputLabel>
                            <Select
                                value={liquidityTierFilter}
                                label="Liquidity Tier"
                                onChange={(e) => { setLiquidityTierFilter(e.target.value); setLiquidityPage(0); }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.values(LiquidityTier).map(tier => (
                                    <MenuItem key={tier} value={tier}>{LiquidityTierLabels[tier]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchLiquidity}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" sx={{ ml: 'auto' }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                startIcon={<CalculateIcon />}
                                onClick={handleLiquidityCalcOpen}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Calculate
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Liquidity Scores Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontFamily, backgroundColor: '#fafafa' } }}>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Strike Price</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Option Type</TableCell>
                            <TableCell align="right">Bid-Ask Width</TableCell>
                            <TableCell align="right">Bid-Ask Width %</TableCell>
                            <TableCell align="right">Volume Score</TableCell>
                            <TableCell align="right">OI Score</TableCell>
                            <TableCell align="right">Composite Score</TableCell>
                            <TableCell>Liquidity Tier</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {liquidityLoading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                            </TableRow>
                        ) : liquidityData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                    <CalculateIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary" sx={{ fontFamily }}>No liquidity scores found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            liquidityData.map(row => (
                                <TableRow key={row.id} hover sx={{ '& td': { fontFamily } }}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{row.symbol || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={OptionTypeLabels[row.option_type] || row.option_type || '-'}
                                            color={OptionTypeColors[row.option_type] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatDecimal(row.bid_ask_width, 4)}</TableCell>
                                    <TableCell align="right">{formatPercent(row.bid_ask_width_pct)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.volume_score, 2)}</TableCell>
                                    <TableCell align="right">{formatDecimal(row.oi_score, 2)}</TableCell>
                                    <TableCell align="right">
                                        <Typography fontWeight={600}>{formatDecimal(row.composite_score, 2)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={LiquidityTierLabels[row.liquidity_tier] || row.liquidity_tier || '-'}
                                            color={LiquidityTierColors[row.liquidity_tier] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={liquidityTotal}
                page={liquidityPage}
                onPageChange={(e, p) => setLiquidityPage(p)}
                rowsPerPage={liquidityPerPage}
                onRowsPerPageChange={(e) => { setLiquidityPerPage(parseInt(e.target.value, 10)); setLiquidityPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ fontFamily }}
            />
        </>
    );

    // ==================== MAIN RENDER ====================

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Gradient Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: `${ACCENT}15` }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: '30%', width: 100, height: 100, borderRadius: '50%', background: `${ACCENT}10` }} />
                <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: '#fff', fontFamily, position: 'relative', zIndex: 1 }}
                >
                    Contract Discovery
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'rgba(255,255,255,0.7)', fontFamily, mt: 1, position: 'relative', zIndex: 1 }}
                >
                    Options universe, expiration calendar, strike selection, and liquidity analysis
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, fontFamily }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                sx={{
                    mb: 3,
                    '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                }}
            >
                <Tab label="Options Universe" />
                <Tab label="Expiration Calendar" />
                <Tab label="Strike Selection" />
                <Tab label="Contract Specs" />
                <Tab label="Liquidity Scores" />
            </Tabs>

            {/* Tab Content */}
            {activeTab === 0 && renderOptionsUniverse()}
            {activeTab === 1 && renderExpirationCalendar()}
            {activeTab === 2 && renderStrikeSelection()}
            {activeTab === 3 && renderContractSpecs()}
            {activeTab === 4 && renderLiquidityScores()}

            {/* ==================== DIALOGS ==================== */}

            {/* Universe Add/Edit Dialog */}
            <Dialog open={universeDialog} onClose={() => setUniverseDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {universeEditing ? 'Edit Universe Entry' : 'Add Universe Entry'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={universeForm.symbol_id || null}
                                onChange={(e, newValue) => setUniverseForm(prev => ({ ...prev, symbol_id: newValue }))}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Underlying Asset Type</InputLabel>
                                <Select
                                    value={universeForm.underlying_asset_type || ''}
                                    label="Underlying Asset Type"
                                    onChange={(e) => setUniverseForm(prev => ({ ...prev, underlying_asset_type: e.target.value }))}
                                >
                                    {Object.values(UnderlyingAssetType).map(type => (
                                        <MenuItem key={type} value={type}>{UnderlyingAssetTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Sector</InputLabel>
                                <Select
                                    value={universeForm.sector || ''}
                                    label="Sector"
                                    onChange={(e) => setUniverseForm(prev => ({ ...prev, sector: e.target.value }))}
                                >
                                    {Object.values(Sector).map(s => (
                                        <MenuItem key={s} value={s}>{SectorLabels[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Has Weeklies</InputLabel>
                                <Select
                                    value={universeForm.has_weeklies ? 'true' : 'false'}
                                    label="Has Weeklies"
                                    onChange={(e) => setUniverseForm(prev => ({ ...prev, has_weeklies: e.target.value === 'true' }))}
                                >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Has Quarterlies</InputLabel>
                                <Select
                                    value={universeForm.has_quarterlies ? 'true' : 'false'}
                                    label="Has Quarterlies"
                                    onChange={(e) => setUniverseForm(prev => ({ ...prev, has_quarterlies: e.target.value === 'true' }))}
                                >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Scan Config (JSON)"
                                value={universeForm.scan_config || ''}
                                onChange={(e) => setUniverseForm(prev => ({ ...prev, scan_config: e.target.value }))}
                                multiline
                                rows={3}
                                placeholder='{"scan_depth": 5, "min_oi": 100}'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUniverseDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUniverseSave}
                        disabled={submitting}
                        sx={{ backgroundColor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily }}
                    >
                        {submitting ? <CircularProgress size={24} /> : (universeEditing ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Universe Delete Dialog */}
            <Dialog open={universeDeleteDialog} onClose={() => setUniverseDeleteDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Universe Entry</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete the universe entry for <strong>{universeDeleteTarget?.symbol}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUniverseDeleteDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleUniverseDelete} sx={{ fontFamily }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Calendar Add Dialog */}
            <Dialog open={calendarDialog} onClose={() => setCalendarDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Expiration Calendar Entry</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={calendarForm.symbol_id || null}
                                onChange={(e, newValue) => setCalendarForm(prev => ({ ...prev, symbol_id: newValue }))}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Expiration Date"
                                type="date"
                                value={calendarForm.expiration_date || ''}
                                onChange={(e) => setCalendarForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Cycle Type</InputLabel>
                                <Select
                                    value={calendarForm.cycle_type || ''}
                                    label="Cycle Type"
                                    onChange={(e) => setCalendarForm(prev => ({ ...prev, cycle_type: e.target.value }))}
                                >
                                    {Object.values(ExpirationCycleType).map(type => (
                                        <MenuItem key={type} value={type}>{ExpirationCycleTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCalendarDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCalendarSave}
                        disabled={submitting}
                        sx={{ backgroundColor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Strike Selection Run Dialog */}
            <Dialog open={strikeDialog} onClose={() => setStrikeDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Run Strike Selection</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={strikeForm.symbol_id || null}
                                onChange={(e, newValue) => setStrikeForm(prev => ({ ...prev, symbol_id: newValue }))}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Strategy Type</InputLabel>
                                <Select
                                    value={strikeForm.strategy_type || ''}
                                    label="Strategy Type"
                                    onChange={(e) => setStrikeForm(prev => ({ ...prev, strategy_type: e.target.value }))}
                                >
                                    {Object.values(StrategyType).map(type => (
                                        <MenuItem key={type} value={type}>{StrategyTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Selection Method</InputLabel>
                                <Select
                                    value={strikeForm.selection_method || ''}
                                    label="Selection Method"
                                    onChange={(e) => setStrikeForm(prev => ({ ...prev, selection_method: e.target.value }))}
                                >
                                    {Object.values(StrikeSelectionMethod).map(method => (
                                        <MenuItem key={method} value={method}>{StrikeSelectionMethodLabels[method]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Option Type</InputLabel>
                                <Select
                                    value={strikeForm.option_type || ''}
                                    label="Option Type"
                                    onChange={(e) => setStrikeForm(prev => ({ ...prev, option_type: e.target.value }))}
                                >
                                    {Object.values(OptionType).map(type => (
                                        <MenuItem key={type} value={type}>{OptionTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Target Delta"
                                type="number"
                                value={strikeForm.target_delta || ''}
                                onChange={(e) => setStrikeForm(prev => ({ ...prev, target_delta: e.target.value }))}
                                inputProps={{ step: 0.01, min: -1, max: 1 }}
                                placeholder="e.g. 0.30"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Target PoP (%)"
                                type="number"
                                value={strikeForm.target_pop || ''}
                                onChange={(e) => setStrikeForm(prev => ({ ...prev, target_pop: e.target.value }))}
                                inputProps={{ step: 0.01, min: 0, max: 1 }}
                                placeholder="e.g. 0.70"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Target Risk/Reward"
                                type="number"
                                value={strikeForm.target_risk_reward || ''}
                                onChange={(e) => setStrikeForm(prev => ({ ...prev, target_risk_reward: e.target.value }))}
                                inputProps={{ step: 0.1, min: 0 }}
                                placeholder="e.g. 2.0"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Expiration Date"
                                type="date"
                                value={strikeForm.expiration_date || ''}
                                onChange={(e) => setStrikeForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStrikeDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleStrikeSave}
                        disabled={submitting}
                        sx={{ backgroundColor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Run Selection'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contract Specs Add/Edit Dialog */}
            <Dialog open={specsDialog} onClose={() => setSpecsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {specsEditing ? 'Edit Contract Specification' : 'Add Contract Specification'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={specsForm.symbol_id || null}
                                onChange={(e, newValue) => setSpecsForm(prev => ({ ...prev, symbol_id: newValue }))}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Underlying Asset Type</InputLabel>
                                <Select
                                    value={specsForm.underlying_asset_type || ''}
                                    label="Underlying Asset Type"
                                    onChange={(e) => setSpecsForm(prev => ({ ...prev, underlying_asset_type: e.target.value }))}
                                >
                                    {Object.values(UnderlyingAssetType).map(type => (
                                        <MenuItem key={type} value={type}>{UnderlyingAssetTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Exercise Style</InputLabel>
                                <Select
                                    value={specsForm.exercise_style || ''}
                                    label="Exercise Style"
                                    onChange={(e) => setSpecsForm(prev => ({ ...prev, exercise_style: e.target.value }))}
                                >
                                    {Object.values(ExerciseStyle).map(style => (
                                        <MenuItem key={style} value={style}>{ExerciseStyleLabels[style]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Settlement Type</InputLabel>
                                <Select
                                    value={specsForm.settlement_type || ''}
                                    label="Settlement Type"
                                    onChange={(e) => setSpecsForm(prev => ({ ...prev, settlement_type: e.target.value }))}
                                >
                                    {Object.values(SettlementType).map(type => (
                                        <MenuItem key={type} value={type}>{SettlementTypeLabels[type]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Contract Multiplier"
                                type="number"
                                value={specsForm.contract_multiplier ?? ''}
                                onChange={(e) => setSpecsForm(prev => ({ ...prev, contract_multiplier: e.target.value }))}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tick Size"
                                type="number"
                                value={specsForm.tick_size ?? ''}
                                onChange={(e) => setSpecsForm(prev => ({ ...prev, tick_size: e.target.value }))}
                                inputProps={{ step: 0.0001, min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Exchange</InputLabel>
                                <Select
                                    value={specsForm.exchange || ''}
                                    label="Exchange"
                                    onChange={(e) => setSpecsForm(prev => ({ ...prev, exchange: e.target.value }))}
                                >
                                    {Object.keys(Exchange).map(key => (
                                        <MenuItem key={key} value={Exchange[key]}>{ExchangeLabels[key]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Currency"
                                value={specsForm.currency || ''}
                                onChange={(e) => setSpecsForm(prev => ({ ...prev, currency: e.target.value }))}
                                placeholder="e.g. USD"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Position Limit"
                                type="number"
                                value={specsForm.position_limit ?? ''}
                                onChange={(e) => setSpecsForm(prev => ({ ...prev, position_limit: e.target.value }))}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Trading Hours</InputLabel>
                                <Select
                                    value={specsForm.trading_hours || ''}
                                    label="Trading Hours"
                                    onChange={(e) => setSpecsForm(prev => ({ ...prev, trading_hours: e.target.value }))}
                                >
                                    {Object.keys(TradingHours).map(key => (
                                        <MenuItem key={key} value={TradingHours[key]}>{TradingHoursLabels[key]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSpecsDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSpecsSave}
                        disabled={submitting}
                        sx={{ backgroundColor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily }}
                    >
                        {submitting ? <CircularProgress size={24} /> : (specsEditing ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contract Specs Delete Dialog */}
            <Dialog open={specsDeleteDialog} onClose={() => setSpecsDeleteDialog(false)}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Contract Specification</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily }}>
                        Are you sure you want to delete the contract spec for <strong>{specsDeleteTarget?.symbol}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSpecsDeleteDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleSpecsDelete} sx={{ fontFamily }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Liquidity Calculate Dialog */}
            <Dialog open={liquidityCalcDialog} onClose={() => setLiquidityCalcDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Calculate Liquidity Scores</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={symbolOptions}
                                getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                                value={liquidityCalcForm.symbol_id || null}
                                onChange={(e, newValue) => setLiquidityCalcForm(prev => ({ ...prev, symbol_id: newValue }))}
                                renderInput={(params) => (
                                    <TextField {...params} label="Symbol" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Expiration Date (optional)"
                                type="date"
                                value={liquidityCalcForm.expiration_date || ''}
                                onChange={(e) => setLiquidityCalcForm(prev => ({ ...prev, expiration_date: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLiquidityCalcDialog(false)} sx={{ fontFamily }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleLiquidityCalcSave}
                        disabled={submitting}
                        sx={{ backgroundColor: ACCENT, '&:hover': { bgcolor: '#d97706' }, fontFamily }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Calculate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContractDiscoveryPage;
